import { useState } from 'react';
import { atualizarOperador, criarOperador } from '../../api/operadores.js';
import type { Operador } from '../../api/operadores.js';
import { mensagemDoErro } from '../../api/erros.js';
import { CampoTexto } from '../../componentes/Campo.js';
import { FormularioLateral } from '../../componentes/FormularioLateral.js';
import { seHouver } from '../../componentes/props.js';

type Props = {
  /** `null` cadastra; com operador, edita. */
  readonly operador: Operador | null;
  readonly aoFechar: () => void;
  readonly aoConcluir: (mensagem: string) => void;
};

/**
 * Operador do app: matrícula e PIN, não login e senha.
 *
 * O PIN vai no corpo da requisição e some da tela junto com o formulário —
 * não fica em estado global, não vai para armazenamento nenhum do navegador e
 * nunca aparece em mensagem de erro.
 */
export function FormularioDeOperador({ operador, aoFechar, aoConcluir }: Props) {
  const [matricula, definirMatricula] = useState(operador?.matricula ?? '');
  const [nome, definirNome] = useState(operador?.nome ?? '');
  const [pin, definirPin] = useState('');
  const [erros, definirErros] = useState<Readonly<Record<string, string>>>({});
  const [erro, definirErro] = useState<string | null>(null);
  const [enviando, definirEnviando] = useState(false);

  const editando = operador !== null;

  function confirmar() {
    const encontrados: Record<string, string> = {};

    if (matricula.trim() === '') {
      encontrados['matricula'] = 'Informe a matrícula.';
    }
    if (nome.trim() === '') {
      encontrados['nome'] = 'Informe o nome.';
    }
    if (!editando && pin.trim() === '') {
      encontrados['pin'] = 'Informe o PIN inicial.';
    }

    definirErros(encontrados);

    if (Object.keys(encontrados).length > 0) {
      return;
    }

    definirErro(null);
    definirEnviando(true);

    const escrita = editando
      ? atualizarOperador(operador.id, { matricula: matricula.trim(), nome: nome.trim() })
      : criarOperador({ matricula: matricula.trim(), nome: nome.trim(), pin: pin.trim() });

    escrita
      .then((salvo) => {
        aoConcluir(
          editando
            ? `Operador ${salvo.matricula} atualizado.`
            : `Operador ${salvo.matricula} cadastrado.`,
        );
      })
      .catch((falha: unknown) => {
        definirEnviando(false);
        definirErro(mensagemDoErro(falha));
      });
  }

  return (
    <FormularioLateral
      titulo={editando ? `Editar ${operador.matricula}` : 'Novo operador do app'}
      subtitulo="Quem entra no coletor com matrícula e PIN, por um aparelho já pareado."
      rotuloConfirmar={editando ? 'Salvar' : 'Cadastrar'}
      aoConfirmar={confirmar}
      aoFechar={aoFechar}
      enviando={enviando}
      {...seHouver('erro', erro)}
    >
      <CampoTexto
        rotulo="Matrícula"
        valor={matricula}
        aoMudar={definirMatricula}
        fonte="mono"
        obrigatorio
        autoCompletar="off"
        ajuda="Única dentro deste armazém."
        {...seHouver('erro', erros['matricula'])}
      />
      <CampoTexto
        rotulo="Nome"
        valor={nome}
        aoMudar={definirNome}
        obrigatorio
        autoCompletar="off"
        {...seHouver('erro', erros['nome'])}
      />
      {!editando && (
        <CampoTexto
          rotulo="PIN"
          valor={pin}
          aoMudar={definirPin}
          tipo="password"
          fonte="mono"
          obrigatorio
          maximoDeCaracteres={8}
          autoCompletar="off"
          ajuda="De 4 a 8 dígitos. Não use sequência nem dígito repetido."
          {...seHouver('erro', erros['pin'])}
        />
      )}
    </FormularioLateral>
  );
}
